"use client";

import React, { useEffect, useState } from "react";
import Image from "next/image";

export default function PWAInstaller() {
  const [mounted, setMounted] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    setMounted(true);
    // 1. Register Service Worker
    if (typeof window !== "undefined" && "serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw.js")
        .then((reg) => console.log("PWA Service Worker registered:", reg.scope))
        .catch((err) => console.warn("PWA Service Worker registration failed:", err));
    }

    // 2. Check if already installed
    if (typeof window !== "undefined") {
      const isStandalone = window.matchMedia("(display-mode: standalone)").matches 
        || (window.navigator as any).standalone === true;
      setIsInstalled(isStandalone);

      // Check user agent to identify iOS
      const ua = window.navigator.userAgent.toLowerCase();
      const iosDevice = /iphone|ipad|ipod/.test(ua);
      setIsIOS(iosDevice);

      // Listen for the Chrome/Android install prompt
      window.addEventListener("beforeinstallprompt", (e) => {
        e.preventDefault();
        setDeferredPrompt(e);
        
        // Show prompt after a short delay for better UX
        const hasDismissed = localStorage.getItem("pwa_prompt_dismissed");
        if (!hasDismissed && !isStandalone) {
          setTimeout(() => {
            setShowPrompt(true);
          }, 4000);
        }
      });

      // Also support custom iOS prompt display if not installed & not dismissed
      const hasDismissed = localStorage.getItem("pwa_prompt_dismissed");
      if (iosDevice && !isStandalone && !hasDismissed) {
        setTimeout(() => {
          setShowPrompt(true);
        }, 4000);
      }
    }
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log(`PWA install prompt choice: ${outcome}`);
      setDeferredPrompt(null);
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    // Remember dismissal for 7 days
    localStorage.setItem("pwa_prompt_dismissed", "true");
  };

  if (!mounted || isInstalled || !showPrompt) return null;

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[9999] w-[92%] max-w-md bg-white rounded-2xl shadow-2xl border border-gray-100 p-5 animate-in slide-in-from-bottom duration-500">
      <div className="flex items-start gap-4">
        {/* App Icon */}
        <div className="relative w-14 h-14 rounded-xl overflow-hidden border border-gray-100 flex-shrink-0 bg-[#FCF8F3] flex items-center justify-center">
          <Image
            src="/images/logo.png"
            alt="MD Brothers"
            width={48}
            height={48}
            className="object-contain"
          />
        </div>

        {/* Text Details */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-bold text-gray-900 truncate">MD Brothers Edu</h3>
            <button 
              onClick={handleDismiss}
              className="text-gray-400 hover:text-gray-600 p-1"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-0.5">Official Web App</p>
          <div className="flex items-center gap-1.5 mt-1.5">
            <div className="flex text-amber-400">
              {"★★★★★".split("").map((star, i) => (
                <span key={i} className="text-sm">★</span>
              ))}
            </div>
            <span className="text-[10px] font-bold text-[#B88E2F] uppercase bg-[#FCF8F3] px-1.5 py-0.5 rounded">
              High Performance
            </span>
          </div>
        </div>
      </div>

      <div className="mt-4 pt-3 border-t border-gray-100">
        {isIOS ? (
          /* iOS Instructions */
          <div className="text-xs text-gray-600 leading-relaxed">
            <p className="font-semibold text-gray-900 mb-1">To install on your iPhone:</p>
            <div className="flex items-center gap-1 flex-wrap">
              <span>1. Tap the Share button</span>
              <svg className="w-5 h-5 text-[#B88E2F] inline" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8M16 6l-4-4-4 4M12 2v13"/>
              </svg>
            </div>
            <p>2. Scroll down and select <span className="font-bold text-gray-900">"Add to Home Screen"</span>.</p>
          </div>
        ) : (
          /* Android / Chrome prompt button */
          <div className="flex items-center gap-3">
            <button
              onClick={handleDismiss}
              className="flex-1 py-2.5 px-4 text-xs font-semibold text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-xl transition"
            >
              Not Now
            </button>
            <button
              onClick={handleInstallClick}
              className="flex-1 py-2.5 px-4 text-xs font-bold text-white bg-[#B88E2F] hover:bg-[#8C6D23] rounded-xl transition shadow-md shadow-[#B88E2F]/20 text-center"
            >
              Install App
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
